import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { api, type ReportScope } from "../lib/api";
import { useDashboardLayout, viewKeyFor } from "../lib/dashboard-layout";
import { useDashboardRefresh } from "../lib/dashboard-refresh";
import { queryKeys } from "../lib/queryKeys";
import { Select } from "../components/ui/Select";
import { cn } from "../components/ui/cn";
import { KpiRow } from "../components/reports/cards/KpiRow";
import { VelocityReport } from "../components/reports/cards/VelocityReport";
import { PipelineReport } from "../components/reports/cards/PipelineReport";
import { ClosedReport } from "../components/reports/cards/ClosedReport";
import { SegmentReport } from "../components/reports/cards/SegmentReport";
import { AgentSegmentReport } from "../components/reports/cards/AgentSegmentReport";
import { WorkloadHeatmap } from "../components/reports/cards/WorkloadHeatmap";
import { CallsReport } from "../components/reports/cards/CallsReport";
import { ResponsesPerTicket } from "../components/reports/cards/ResponsesPerTicket";
import { OverdueTasks } from "../components/reports/cards/OverdueTasks";
import type { User } from "../types";

/**
 * Support and operational analytics.
 *
 * Three things shape the whole page.
 *
 * **No page-level date filter.** Every card carries its own. The questions a
 * dashboard answers have different natural periods — "what is waiting right
 * now" and "how did last quarter go" are both top-of-page questions — and one
 * filter over all of them makes each card wrong for somebody.
 *
 * **Every card wears the same chrome.** Collapse, date range, chart switcher
 * and colour controls come from `ReportCard`, so they cannot drift apart card
 * by card. What each card supplies is its data and which visualisations that
 * data can honestly carry.
 *
 * **The view is who you are looking at, not what you are looking at.** Managers
 * switch between the whole desk, a team and one agent without navigating.
 * Agents switch between themselves and a team they are in — and no further:
 * there is no route here to a named colleague's figures, and their own view
 * carries the team aggregate beside it so a slow week can be told from a normal
 * one. That is the comparison the request was for, and it is deliberately
 * against the team as one number rather than a ranking of the people in it.
 *
 * The restriction is enforced on the server, by `DashboardScopeGuard`, because
 * a UI-only rule is not one — a selector that exists is a selector whose query
 * string somebody will edit. What this page decides is what is worth *offering*
 * (an agent's own teams, from `myTeams`); what is permitted is decided there.
 */

type View = "all" | "team" | "agent";

export function Dashboard({ user }: { user: User }) {
  /*
   * "Manager" in the spec is Pulse's `admin`. There is no reporting line in the
   * data — no manager column, no team lead — so inventing a third role here
   * would be a permission with nothing behind it.
   */
  const isManager = user.role === "admin";

  /*
   * Which view you are on lives in the URL, not in component state.
   *
   * It was `useState`, so a reload dropped a manager back onto the whole desk
   * and an agent back onto themselves -- and reloading a dashboard is what
   * people do to it. The URL rather than storage, for the reason
   * `ticket-filters.ts` gives at length: which figures you are looking at is
   * part of *where you are*, so putting it there survives the reload, steps
   * through back and forward, and makes the address bar a description somebody
   * can paste to a colleague. A per-browser copy would get none of that and
   * would then disagree with the address bar.
   *
   * Replaced rather than pushed, as the filter bar does: choosing a team is
   * two or three goes at a dropdown, and each one should not be a history entry
   * between here and wherever you came from.
   */
  const [params, setParams] = useSearchParams();

  const view: View = readView(params.get("view"), isManager);
  const teamId = params.get("teamId") ?? "";
  /* An agent is pinned to themselves in the agent view, and the selector is
     absent rather than disabled: a control you can see and cannot use is worse
     than no control. */
  const userId = isManager ? (params.get("userId") ?? "") : String(user.id);

  const setParam = useCallback(
    (key: string, value: string) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const setView = useCallback(
    (next: View) => setParam("view", next),
    [setParam],
  );
  const setTeamId = useCallback(
    (next: string) => setParam("teamId", next),
    [setParam],
  );
  const setUserId = useCallback(
    (next: string) => setParam("userId", next),
    [setParam],
  );

  /*
   * Now fetched for everybody, not only managers: an agent needs `myTeams` to
   * know whether they have a team view to offer at all, and which.
   */
  const filters = useQuery({
    queryKey: queryKeys.dashboardFilters,
    queryFn: api.dashboardFilters,
    staleTime: 5 * 60_000,
  });

  const agents = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
    enabled: isManager,
  });

  /* Every team for a manager, their own for anybody else. The server refuses
     the difference rather than trusting this -- see `DashboardScopeGuard`. */
  const teamOptions = isManager
    ? (filters.data?.teams ?? [])
    : (filters.data?.myTeams ?? []);

  /*
   * One team is not a choice, so it is not presented as one.
   *
   * Most agents are in exactly one team, and asking them to pick it out of a
   * menu of one before the page will show them anything is a step that exists
   * only because the manager's version of this control needed it.
   */
  const soleTeam =
    !isManager && teamOptions.length === 1 ? String(teamOptions[0]!.id) : "";
  const chosenTeam = teamId || soleTeam;

  const scope: ReportScope = {
    ...(view === "team" && chosenTeam ? { teamId: Number(chosenTeam) } : {}),
    ...(view === "agent" && userId ? { userId: Number(userId) } : {}),
  };

  const layout = useDashboardLayout(viewKeyFor(scope));

  /* Only meaningful about one agent, and it is what makes a personal view a
     self-assessment rather than a scoreboard. */
  const compareTeam = view === "agent" && Boolean(scope.userId);

  /*
   * One clock for the page. Every card shares a staleness setting and this
   * moves all of them together, so two readings of the same underlying rows
   * cannot be minutes apart without anybody being able to tell.
   */
  const refresh = useDashboardRefresh();

  /* The same key the workload card asks under, so this is one request. */
  const telephony = useQuery({
    queryKey: ["integrations", "telephony"],
    queryFn: api.telephonyStatus,
    staleTime: 30 * 60_000,
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
        {/*
          Pinned on a desktop, because the four figures at the top are the ones
          somebody glances back at while reading everything below, and
          scrolling them away means scrolling back up to check whether the
          queue moved.

          Not on a phone. The same block that costs a strip of a monitor is
          four stacked tiles and a header on a handset -- most of the screen,
          permanently, leaving a slot to read the actual dashboard through.
          The trade only works while the pinned thing is small relative to the
          viewport, and below `lg` it is not.
        */}
        {/* Opaque. It was `bg-app/95` over a blur, which is a frosted bar --
            and a frosted bar is one you can see the cards sliding about
            behind. A pinned header that shows its own page through itself
            reads as a rendering fault rather than as depth. */}
        <div className="-mx-4 bg-app px-4 pt-4 sm:-mx-6 sm:px-6 lg:sticky lg:top-0 lg:z-20">
          <header className="mb-3 flex flex-wrap items-center gap-2">
            <h1 className="text-base font-semibold text-content">
              {view === "all"
                ? "The desk"
                : view === "team"
                  ? (namedTeam(teamOptions, chosenTeam) ?? "A team")
                  : scope.userId === user.id
                    ? "About you"
                    : (namedAgent(agents.data, userId) ?? "One agent")}
            </h1>

            {isManager ? (
              <>
                <ViewToggle
                  view={view}
                  onChange={setView}
                  options={[
                    ["all", "All"],
                    ["team", "By team"],
                    ["agent", "By agent"],
                  ]}
                />

                {/* In place, never a navigation: the cards are keyed on the
                    scope, so choosing a different agent refetches rather than
                    rebuilding the page. */}
                {view === "team" ? (
                  <Select
                    value={teamId}
                    onChange={setTeamId}
                    label="Which team"
                    options={[
                      { value: "", label: "Choose a team…" },
                      ...teamOptions.map((team) => ({
                        value: String(team.id),
                        label: team.name,
                      })),
                    ]}
                  />
                ) : null}

                {view === "agent" ? (
                  <Select
                    value={userId}
                    onChange={setUserId}
                    label="Which agent"
                    options={[
                      { value: "", label: "Choose an agent…" },
                      ...(agents.data ?? []).map((agent) => ({
                        value: String(agent.id),
                        label: agent.name,
                      })),
                    ]}
                  />
                ) : null}
              </>
            ) : (
              <>
                {/*
                  Two views for an agent, and only two. "All" is deliberately
                  absent even though the server would answer it: the desk-wide
                  numbers are already the desk's, but this page is the one an
                  agent opens about their own work, and a third tab makes the
                  choice look like a report builder rather than "me, or us".

                  It appears at all only when they are in a team. An agent in
                  none has nothing to compare against, and a toggle whose other
                  half is empty is a control that only ever disappoints.
                */}
                {teamOptions.length > 0 ? (
                  <ViewToggle
                    view={view}
                    onChange={setView}
                    options={[
                      ["agent", "You"],
                      ["team", teamOptions.length > 1 ? "Your teams" : "Your team"],
                    ]}
                  />
                ) : null}

                {view === "team" && teamOptions.length > 1 ? (
                  <Select
                    value={chosenTeam}
                    onChange={setTeamId}
                    label="Which of your teams"
                    options={[
                      { value: "", label: "Choose a team…" },
                      ...teamOptions.map((team) => ({
                        value: String(team.id),
                        label: team.name,
                      })),
                    ]}
                  />
                ) : null}

                <span className="text-xs text-faint">
                  {view === "team"
                    ? "Your team as a whole — nobody in it is broken out by name."
                    : "Your own work, with your team's median for context."}
                </span>
              </>
            )}
          </header>

          <KpiRow layout={layout} scope={scope} />

          {/*
            Under the figures rather than beside the title: it is about the
            whole page, and a reader checks it after noticing a number rather
            than before looking at one.
          */}
          <div className="mt-2 flex items-baseline gap-2 text-[11px] text-faint">
            <span>
              {refresh.busy
                ? "Refreshing…"
                : refresh.refreshedAt
                  ? `Updated ${refresh.refreshedAt.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : "Figures refresh every minute"}
            </span>
            <button
              type="button"
              onClick={refresh.refresh}
              disabled={refresh.busy}
              className="text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-400"
            >
              Refresh all
            </button>
          </div>

          <div className="h-3" />
        </div>

        {view === "team" && !chosenTeam ? (
          <Empty>Choose a team to see its figures.</Empty>
        ) : view === "agent" && !userId ? (
          <Empty>Choose an agent to see their figures.</Empty>
        ) : (
          <div className="flex flex-col gap-3">
            {/*
              Above everything, because it is the only thing on the page that
              is a call to action rather than a reading. It renders nothing at
              all when nothing is late -- a permanent banner showing zero is a
              banner people stop seeing.
            */}
            <OverdueTasks scope={scope} />

            {/*
              Agent views only, and that is the feature rather than a
              limitation. On the desk-wide view this card is a league table of
              named colleagues ranked by a number none of them agreed to be
              ranked on, which is the screen Pulse deliberately does not have --
              the same reason the team comparison returns one aggregate and
              never a breakdown by person. Read about yourself it is a
              self-assessment; read about everybody it is a scoreboard.

              The All and By team views are untouched, so nothing anybody
              already reads has moved.
            */}
            {view === "agent" ? (
              <ResponsesPerTicket isManager={isManager} layout={layout} scope={scope} />
            ) : null}

            <VelocityReport
              isManager={isManager}
              layout={layout}
              scope={scope}
              compareTeam={compareTeam}
            />

            <div className="grid gap-3 lg:grid-cols-2">
              <PipelineReport isManager={isManager} layout={layout} scope={scope} />
              <ClosedReport isManager={isManager} layout={layout} scope={scope} />
            </div>

            <SegmentReport
              isManager={isManager}
              layout={layout}
              scope={scope}
              compareTeam={compareTeam}
            />

            {/*
              Managers only, for the reason given on ResponsesPerTicket above
              and enforced on the server rather than here: it is a table of
              named colleagues ranked by a number, and an agent's own view
              compares them against a team aggregate and never a named
              individual. Directly under the pipeline breakdown because it is
              the other half of the same question -- where the desk is slow,
              and whose replies those are.
            */}
            {isManager ? (
              <AgentSegmentReport isManager={isManager} layout={layout} scope={scope} />
            ) : null}

            {/*
              Only with a phone system connected. Without one, the workload
              card below already names the missing call figures, and a second
              card saying the same thing is one more panel to scroll past.

              On every view, an agent's own included: how many calls you took
              is a fact about your work like a reply time is, and the scope
              guard keeps it to yourself and your team as it does for tickets.
            */}
            {telephony.data?.enabled ? (
              <CallsReport isManager={isManager} layout={layout} scope={scope} />
            ) : null}

            {/*
              Tickets, calls and Lane tasks per person, last because it is the
              widest reading: the question it answers -- is a quiet ticket count
              somebody on the phone all morning -- needs the cards above to
              have been read first.
            */}
            <WorkloadHeatmap isManager={isManager} layout={layout} scope={scope} />

            {/*
              No team audit here. It has its own page, and it is a different
              job done by a different person: this dashboard answers "where is
              the desk right now" and gets read daily, where the audit is a
              weekly review of individual work. Folding it in made it look like
              one more chart to skim past.
            */}
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-line py-12 text-center text-xs text-faint">
      {children}
    </div>
  );
}

/**
 * The row of view buttons, drawn once for two different sets of views.
 *
 * A manager's three and an agent's two are the same control with different
 * labels, and two copies of the class list is one copy too many -- the pressed
 * state and the rounded ends are exactly the sort of thing that drifts apart
 * when one of them is edited.
 */
function ViewToggle({
  view,
  onChange,
  options,
}: {
  view: View;
  onChange: (view: View) => void;
  options: readonly (readonly [View, string])[];
}) {
  return (
    <div className="flex rounded-md border border-line" role="group">
      {options.map(([option, label]) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={view === option}
          className={cn(
            "px-2.5 py-1 text-xs first:rounded-l-md last:rounded-r-md",
            view === option
              ? "bg-brand-600 text-white"
              : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * The view named in the URL, or the one this person starts on.
 *
 * Anything unrecognised falls back rather than being refused, the same bargain
 * `ticket-filters.ts` makes: these arrive from an address bar somebody may have
 * edited or a link that outlived a rename, and a dashboard that renders nothing
 * because of a typo in a query string is worse than one that shows the default.
 *
 * "all" is not an agent's to ask for by URL either. The toggle does not offer
 * it to them, and a hand-typed `?view=all` should land where the control would
 * -- the server decides what they may *read*, but this decides what this page
 * is willing to be.
 */
function readView(value: string | null, isManager: boolean): View {
  const fallback: View = isManager ? "all" : "agent";
  if (value !== "all" && value !== "team" && value !== "agent") return fallback;
  if (value === "all" && !isManager) return fallback;
  return value;
}

function namedTeam(
  teams: { id: number; name: string }[],
  teamId: string,
): string | null {
  if (!teamId) return null;
  return teams.find((team) => String(team.id) === teamId)?.name ?? null;
}

function namedAgent(
  agents: { id: number; name: string }[] | undefined,
  userId: string,
): string | null {
  if (!userId) return null;
  return agents?.find((agent) => String(agent.id) === userId)?.name ?? null;
}
